import type { Post, Category, PostListResponse, User, PublicProfile, RobotToken, FollowStatus, FollowUser, CommunityStatus, ArticleComment, NotificationList, ArticleCollection } from "./types";

const API_BASE = "/api/v1";

interface ApiResponse<T> {
  code: number;
  message: string;
  count?: number;
  currentTime?: string;
  data: T;
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (!res.ok) throw new Error(json.message || `请求失败：${res.status}`);
  if (json.code !== 2000) {
    if (res.status === 401) localStorage.removeItem("token");
    throw new Error(json.message || "请求失败");
  }
  return json.data;
}

export interface FetchPostsParams {
  keyword?: string;
  category?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchPosts(params: FetchPostsParams = {}): Promise<PostListResponse> {
  const search = new URLSearchParams();
  if (params.keyword) search.set("keyword", params.keyword);
  if (params.category && params.category !== "全部") search.set("category", params.category);
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  const query = search.toString();
  return request<PostListResponse>(`${API_BASE}/documents?${query}`);
}

export async function fetchCategories(): Promise<Category[]> {
  return request<Category[]>(`${API_BASE}/categories`);
}

export function createCategory(data: Pick<Category, "name" | "icon" | "sortOrder">): Promise<{ id: number; name: string }> {
  return request(`${API_BASE}/categories`, { method: "POST", body: JSON.stringify(data) });
}

export function updateCategory(id: number, data: Pick<Category, "name" | "icon" | "sortOrder">): Promise<void> {
  return request(`${API_BASE}/categories/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export function deleteCategory(id: number): Promise<void> {
  return request(`${API_BASE}/categories/${id}`, { method: "DELETE" });
}

export function organizeArticle(source: string, categories: string[]): Promise<{ reply: string }> {
  const prompt = `请将下面的原始素材整理成一篇结构清晰的中文 Markdown 文章。只返回合法 JSON，不要代码围栏或额外说明。JSON 字段必须为：title（标题）、summary（100字内摘要）、category（只能从分类列表选择，没有合适的则为空字符串）、tags（2到6个字符串）、content（完整 Markdown 正文）。\n可选分类：${categories.join("、") || "无"}\n\n原始素材：\n${source}`;
  return request(`${API_BASE}/ai/chat`, { method: "POST", body: JSON.stringify({ messages: [{ role: "user", content: prompt }] }) });
}
export function askAi(content:string):Promise<{reply:string}>{return request(`${API_BASE}/ai/chat`,{method:"POST",body:JSON.stringify({messages:[{role:"user",content}]})});}
export function summarizeArticle(documentId:number|string):Promise<{reply:string;fullAccess:boolean}>{return request(`${API_BASE}/documents/${documentId}/ai-summary`,{method:"POST"});}

export async function fetchPost(id: number | string): Promise<Post> {
  const result = await request<{ document: Post }>(`${API_BASE}/documents/${id}`);
  return result.document;
}

export interface AuthResult { token: string; user: User }
export interface AuthStatus { exists: boolean; hasAdmin: boolean; registrationEnabled: boolean }

export async function login(email: string, password: string): Promise<AuthResult> {
  return request<AuthResult>(`${API_BASE}/auth/login`, {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function register(username: string, email: string, password: string, confirmPassword: string): Promise<AuthResult> {
  return request<AuthResult>(`${API_BASE}/auth/register`, { method: "POST", body: JSON.stringify({ username, email, password, confirmPassword }) });
}

export async function setup(username: string, email: string, password: string, confirmPassword: string): Promise<AuthResult> {
  return request<AuthResult>(`${API_BASE}/auth/setup`, { method: "POST", body: JSON.stringify({ username, email, password, confirmPassword }) });
}

export function checkAuthStatus(): Promise<AuthStatus> {
  return request<AuthStatus>(`${API_BASE}/auth/check`);
}

export async function getCurrentUser(): Promise<User> {
  const result = await request<{ user: User }>(`${API_BASE}/user/profile`);
  return result.user;
}

export function createPost(data: { title: string; content: string; summary?: string; category?: string; collectionId?:number|null; tags?: string[]; visibility: "public" | "private" }) {
  return request<{ id: number }>(`${API_BASE}/documents`, {
    method: "POST",
    body: JSON.stringify({ ...data, pricingType: "free", previewLength: 500 }),
  });
}
export function updatePost(id:number|string,data:{title:string;content:string;summary?:string;category?:string;collectionId?:number|null;tags?:string[];visibility:"public"|"private"}):Promise<void>{return request(`${API_BASE}/documents/${id}`,{method:"PUT",body:JSON.stringify(data)});}

export function fetchPublicProfile(id: number | string, page = 1): Promise<PublicProfile> {
  return request<PublicProfile>(`${API_BASE}/users/${id}?page=${page}&pageSize=12`);
}

export async function fetchRobotTokens(includeToken = false): Promise<RobotToken[]> {
  const query = includeToken ? "?includeToken=1" : "";
  type RawRobotToken = Omit<RobotToken,"permissions"> & { permissions?: unknown };
  const result = await request<{ list: RawRobotToken[] }>(`${API_BASE}/robot-tokens${query}`);
  return (Array.isArray(result.list)?result.list:[]).map(token=>{
    let values:unknown[]=Array.isArray(token.permissions)?token.permissions:[];
    if(typeof token.permissions==="string"){
      const source=token.permissions.trim();
      if(source.startsWith("[")){try{const parsed=JSON.parse(source);values=Array.isArray(parsed)?parsed:[];}catch{values=source.split(",");}}
      else values=source.split(",");
    }
    const permissions=[...new Set(values.map(String).map(value=>value.trim()).filter((value):value is RobotToken["permissions"][number]=>["read","write","delete"].includes(value)))];
    return {...token,permissions};
  });
}
export function createRobotToken(data: { robotName: string; description?: string; permissions: string[]; expiresAt?: string }): Promise<{ token: string }> {
  return request(`${API_BASE}/robot-tokens`, { method: "POST", body: JSON.stringify(data) });
}
export function deleteRobotToken(id: number): Promise<void> { return request(`${API_BASE}/robot-tokens/${id}`, { method: "DELETE" }); }

export async function uploadAvatar(file: File): Promise<{ url: string }> {
  const body=new FormData();body.append("file",file);const token=localStorage.getItem("token");
  const response=await fetch(`${API_BASE}/upload`,{method:"POST",headers:token?{Authorization:`Bearer ${token}`}:{},body});
  const json=await response.json() as ApiResponse<{url:string}>;if(!response.ok||json.code!==2000)throw new Error(json.message||"头像上传失败");return json.data;
}
export function updateProfile(data:{username?:string;avatar?:string}):Promise<void>{return request(`${API_BASE}/user/profile`,{method:"PUT",body:JSON.stringify(data)});}
export interface AiProvider { key:string; name?:string; logged_in?:boolean; account_label?:string }
export interface AiProviders { enabled_provider?:string; providers:AiProvider[]; runtime?:{name?:string;version?:string} }
export function fetchAiProviders():Promise<AiProviders>{return request(`${API_BASE}/ai/providers`);}
export function startAiLogin(provider:string):Promise<{id:string;status:string;web_url?:string;qr_data?:string;qr_url?:string;qr_mime?:string}>{return request(`${API_BASE}/ai/providers/${provider}/login`,{method:"POST"});}
export function fetchAiLogin(taskId:string):Promise<Record<string,unknown>>{return request(`${API_BASE}/ai/login/${taskId}`);}
export function cancelAiLogin(taskId:string):Promise<void>{return request(`${API_BASE}/ai/login/${taskId}`,{method:"DELETE"});}
export function enableAiProvider(provider:string):Promise<void>{return request(`${API_BASE}/ai/providers/${provider}`,{method:"PUT"});}
export function removeAiProvider(provider:string):Promise<void>{return request(`${API_BASE}/ai/providers/${provider}`,{method:"DELETE"});}

export function fetchFollowStatus(userId:number|string):Promise<FollowStatus>{return request(`${API_BASE}/users/${userId}/follow-status`);}
export function toggleFollow(userId:number|string):Promise<{followed:boolean}>{return request(`${API_BASE}/users/${userId}/follow`,{method:"POST"});}
export async function fetchFollows(userId:number|string,kind:"followers"|"following"):Promise<FollowUser[]>{const result=await request<{list:FollowUser[]}>(`${API_BASE}/users/${userId}/follows?kind=${kind}`);return result.list;}
export function fetchCommunity(documentId:number|string):Promise<CommunityStatus>{return request(`${API_BASE}/documents/${documentId}/community`);}
export function toggleLike(documentId:number|string):Promise<{liked:boolean;likeCount:number}>{return request(`${API_BASE}/documents/${documentId}/like`,{method:"POST"});}
export async function fetchComments(documentId:number|string):Promise<ArticleComment[]>{const result=await request<{list:ArticleComment[]}>(`${API_BASE}/documents/${documentId}/comments`);return result.list;}
export function createComment(documentId:number|string,content:string,parentId?:number):Promise<{id:number}>{return request(`${API_BASE}/documents/${documentId}/comments`,{method:"POST",body:JSON.stringify({content,parentId})});}
export function deleteComment(commentId:number):Promise<void>{return request(`${API_BASE}/comments/${commentId}`,{method:"DELETE"});}
export function fetchNotifications(page=1):Promise<NotificationList>{return request(`${API_BASE}/notifications?page=${page}&pageSize=30`);}
export function markNotificationRead(id:number):Promise<void>{return request(`${API_BASE}/notifications/${id}/read`,{method:"PUT"});}
export function markAllNotificationsRead():Promise<void>{return request(`${API_BASE}/notifications/read-all`,{method:"PUT"});}
export async function fetchMyCollections():Promise<ArticleCollection[]>{const result=await request<{list:ArticleCollection[]}>(`${API_BASE}/user/collections`);return result.list;}
export function createCollection(name:string,description:string):Promise<{id:number}>{return request(`${API_BASE}/user/collections`,{method:"POST",body:JSON.stringify({name,description})});}
export function updateCollection(id:number,name:string,description:string):Promise<void>{return request(`${API_BASE}/user/collections/${id}`,{method:"PUT",body:JSON.stringify({name,description})});}
export function deleteCollection(id:number):Promise<void>{return request(`${API_BASE}/user/collections/${id}`,{method:"DELETE"});}
export async function fetchMyDocuments():Promise<Post[]>{const result=await request<{list:Post[]}>(`${API_BASE}/user/documents`);return result.list;}
export function fetchUserCollections(userId:number|string):Promise<{user:{id:number;username:string;avatar?:string};collections:ArticleCollection[]}>{return request(`${API_BASE}/users/${userId}/collections`);}
export function fetchUserCollection(userId:number|string,collectionId:number|string):Promise<{user:{id:number;username:string;avatar?:string};collection:ArticleCollection;documents:Post[]}>{return request(`${API_BASE}/users/${userId}/collections/${collectionId}`);}
